// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import { GetStaticComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { KeyboardEvent, KeyboardEventHandler, useState } from 'react';
// Components
import Component from 'src/helpers/Component/Component';
import { ProductCarouselTheme } from './ProductCarousel.theme';
import { BodyCopy } from 'src/helpers/BodyCopy';
import Headline from 'src/helpers/Headline/Headline';
import { RenderSlider } from './RenderProductSlider.helper';
import { ComponentProps } from 'lib/types/component-props';
import { Button } from 'src/helpers/Button';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import { CustomArrowProps } from 'react-slick';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
export type ProductCarouselProps =
  Feature.EnterpriseWeb.Enterprise.Components.Product.ProductCarousel & {
    fields?: {
      children?: Feature.EnterpriseWeb.Enterprise.Components.Product.ProductCarouselSlide;
    };
  } & ComponentProps;

const ProductCarousel = (props: ProductCarouselProps) => {
  // Backup method to convert field data if it comes from GraphQL integrated data source since
  // getStaticProps isn't invoked when this component is rendered using itemrendering.
  const fields = props?.fields?.data?.item
    ? mapGraphQlDataToProps(props?.fields)?.fields
    : props?.fields;

  const { themeData } = useTheme(ProductCarouselTheme);
  const slidesData = fields.children;

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const ArrowPrev = (props: CustomArrowProps) => {
    const { onClick } = props;

    return (
      <div className={themeData.classes.arrowPrevWrapperClass}>
        <div className={themeData.classes.arrowPrevButtonWrapperClass}>
          <button
            className={themeData.classes.arrowButtonClass}
            onClick={onClick}
            onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
              if (e.code === 'Enter' || e.code === 'Space') {
                onClick && (onClick as unknown as KeyboardEventHandler<HTMLButtonElement>)(e);
              }
            }}
            tabIndex={0}
          >
            <SvgIcon icon="arrow-left" size="lg" />
          </button>
        </div>
      </div>
    );
  };

  const ArrowNext = (props: CustomArrowProps) => {
    const { onClick } = props;
    return (
      <div className={themeData.classes.arrowNextWrapperClass}>
        <div className={themeData.classes.arrowPrevButtonWrapperClass}>
          <button
            className={themeData.classes.arrowButtonClass}
            onClick={onClick}
            onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
              if (e.code === 'Enter' || e.code === 'Space') {
                onClick && (onClick as unknown as KeyboardEventHandler<HTMLButtonElement>)(e);
              }
            }}
            tabIndex={0}
          >
            <SvgIcon icon="arrow-right" size="lg" />
          </button>
        </div>
      </div>
    );
  };

  const sliderSettings = {
    className: 'center product-carousel',
    centerMode: true,
    infinite: true,
    dots: true,
    arrows: true,
    slidesToScroll: 1,
    variableWidth: true,
    enableNumberedPagination: false,
    pauseOnFocus: true,
    pauseOnHover: true,
    dotsClass: 'slick-dots static',
    useTransform: false,
    prevArrow: <ArrowPrev />,
    nextArrow: <ArrowNext />,
    speed: 400,
    beforeChange: (_currentSlide: number, nextSlide: number) => {
      setCurrentSlideIndex(nextSlide);
    },
  };

  return (
    <Component variant="full" padding={'px-0'} dataComponent="general/productcarousel" {...props}>
      <div className="col-span-12">
        <div className="px-m md:max-w-screen-lg lg:mx-auto">
          <Headline defaultTag="h2" {...props} />
          <BodyCopy {...props} />
        </div>
      </div>
      <div className={themeData.classes.columnClass}>
        <div className="product-carousel z-0">
          <RenderSlider
            currentSlideIndex={currentSlideIndex}
            slidesData={slidesData}
            sliderSettings={sliderSettings}
          />
        </div>
        <div className={themeData.classes.exploreButtonClass}>
          <div>
            <Button
              field={fields?.cta1Link}
              variant={fields?.cta1Style}
              icon={fields?.cta1Icon}
              classes={fields?.cta1Style}
            ></Button>
          </div>
        </div>
      </div>
    </Component>
  );
};
type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      fields: ItemFieldResult[];
    }>;
  };
}>;

const mapGraphQlDataToProps = (fieldData: IntegratedGraphQl) => {
  const result = {
    fields: {
      ...mapItemFieldResultsToObject(fieldData.data.item.fields),
      children: mapSearchResults(fieldData.data.item.children, (child) => ({
        fields: {
          ...mapItemFieldResultsToObject(child.fields),
        },
      })),
    },
  };

  return result;
};

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;

  const result = mapGraphQlDataToProps(fields);

  return result;
};

export default withDatasourceCheck()<ProductCarouselProps>(ProductCarousel);
